import { Box } from '@lego/klik-ui';
import type { ChangeEvent, KeyboardEvent, ReactElement, ReactNode } from 'react';
import { DetailsHeader } from '@/components/common';
import { EditableDetailsSection } from '@/components/common/details/EditableDetailsSection';
import { EditableDetailsHeader } from '@/components/common/headers/EditableDetailsHeader';
import type { DetailSectionProps } from './DetailsBox';

type Props = {
  title: string;
  sections: DetailSectionProps[];
  additionalComponent?: ReactElement;
  editableFields: string[];
  editingKey: string | null;
  editedValue: string;
  isSaving: boolean;
  onValueChange: (value: string) => void;
  onStartEdit: (key: string, currentValue: ReactNode) => void;
  onCancelEdit: () => void;
  onSave: (key: string, value?: string) => Promise<void>;
};

export const EditableDetailsBox = ({
  title,
  sections,
  additionalComponent,
  editableFields,
  editingKey,
  editedValue,
  isSaving,
  onValueChange,
  onStartEdit,
  onCancelEdit,
  onSave,
}: Props) => {
  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onValueChange(e.target.value);
  };

  const handleKeyDown = async (
    e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string
  ) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      await onSave(key, (e.target as HTMLInputElement).value);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancelEdit();
    }
  };

  return (
    <Box background="white" boxShadow="md" padding={6} rounded="md" width={{ base: '100%' }}>
      {editableFields.includes('Name') ? (
        <EditableDetailsHeader
          editedValue={editedValue}
          editingKey={editingKey}
          isSaving={isSaving}
          onCancel={onCancelEdit}
          onInputChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onSave={onSave}
          onStartEdit={onStartEdit}
          title={title}
        />
      ) : (
        <DetailsHeader title={title} />
      )}

      {sections.map((section, idx) => (
        <EditableDetailsSection
          editableFields={editableFields}
          editedValue={editedValue}
          editingKey={editingKey}
          isSaving={isSaving}
          key={section.title || `section-${idx}`}
          onCancel={onCancelEdit}
          onInputChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onSave={onSave}
          onStartEdit={onStartEdit}
          onValueChange={onValueChange}
          section={section}
        />
      ))}

      {additionalComponent}
    </Box>
  );
};
